import logger from '@/utils/logger';
import { create } from 'zustand';
import { FILTER_NAMES } from '../types/mappers/filter';
import { Filter } from '../types/models/filter';
import { useProductStore } from './productStore';

// 선택된 옵션 타입 정의
export interface SelectedFilterOption {
  filterId: string;
  filterName: string;
  optionName: string;
  selectedAt: number;
}

interface FilterStoreState {
  // 상태
  queryFilters: Record<string, string>;
  filters: Filter[];
  tempFilters: Filter[];
  isLoading: boolean;
  error: Error | null;
  searchQueries: Record<string, string>;
  selectedTempCount: number;
  selectedTempOptions: SelectedFilterOption[];
  appliedFilterOrder: SelectedFilterOption[]; // 적용된 필터의 순서

  // 액션
  setQueryFilters: (filters: Record<string, string>) => void;
  setFilters: (filters: Filter[] | null) => void;
  unCheckFilter: (filterId: string, optionName: string) => void;
  resetFilters: () => void;
  applyTempFilters: () => void;
  restoreOriginalFilters: () => void;
  resetTempFilters: () => void;
  resetTempFilter: (filterId: string) => void;
  handleChangeCheckedTempFilter: (
    filterId: string,
    optionName: string,
  ) => Promise<void>;
  getSelectedOptions: (
    mode: 'filters' | 'tempFilters',
  ) => SelectedFilterOption[];
}

// 선택된 옵션 목록 계산 함수
const calculateSelectedOptions = (
  filters: Filter[],
): SelectedFilterOption[] => {
  const options = filters.flatMap((filter) =>
    filter.options
      .filter((option) => option.selected)
      .map((option) => ({
        filterId: filter.id,
        filterName: filter.name,
        optionName: option.name,
        // @ts-expect-error: selectedAt 속성이 FilterOption에 없지만 런타임에는 존재할 수 있음
        selectedAt: option.selectedAt || 0,
      })),
  );

  // 선택 순서대로 정렬 (오름차순 - 먼저 선택된 것이 앞으로)
  return options.sort((a, b) => a.selectedAt - b.selectedAt);
};

const toRecord = (filters: Filter[]): Record<string, string> => {
  logger.info('toRecord', filters);

  const record = filters.reduce(
    (acc, filter) => {
      const selectedOptions = filter.options
        .filter((o) => o.selected)
        .map((o) => o.name)
        .join(',');

      acc[filter.id] = selectedOptions;
      return acc;
    },
    {} as Record<string, string>,
  );

  logger.info('toRecord : after', record);

  return record;
};

const deepEqualFilter = (filter1: Filter, filter2: Filter): boolean => {
  if (filter1 === filter2) return true;
  if (!filter1 || !filter2) return false;
  if (filter1.id !== filter2.id) return false;
  if (!Array.isArray(filter1.options) || !Array.isArray(filter2.options))
    return false;
  if (filter1.options.length !== filter2.options.length) return false;

  return filter1.options.every((option1, index) => {
    const option2 = filter2.options[index];
    return (
      option1.name === option2.name && option1.selected === option2.selected
    );
  });
};

const deepEqualFilters = (filters1: Filter[], filters2: Filter[]): boolean => {
  if (filters1.length !== filters2.length) return false;
  return filters1.every((filter1, index) =>
    deepEqualFilter(filter1, filters2[index]),
  );
};

const convertToFilter = (filters: Record<string, string>): Filter[] => {
  return Object.entries(filters).map(([key, values]) => ({
    id: key,
    name: FILTER_NAMES[key],
    options: values.split(',').map((value: string) => ({
      name: value,
      selected: true, // 쿼리 필터는 모두 선택된 상태로 추가
      selectedAt: 0, // 초기값
    })),
  }));
};

const extendFilters = (
  originalFilters: Filter[],
  newFilters: Filter[],
): Filter[] => {
  logger.info('extendFilters', originalFilters, newFilters);
  // 새 배열을 반환
  const updatedFilters = newFilters.reduce(
    (acc, newFilter) => {
      const originalFilterIndex = acc.findIndex((f) => f.id === newFilter.id);

      if (originalFilterIndex === -1) {
        // 존재하지 않는 필터는 새로 추가
        acc.push(newFilter);
      }

      return acc;
    },
    [...originalFilters],
  );

  logger.info('-- return extendFilters', updatedFilters);
  return updatedFilters;
};

const applySelectiveUpdates = (
  oldFilters: Filter[],
  newFilters: Filter[],
): boolean => {
  for (const oldFilter of oldFilters) {
    const newFilter = newFilters.find((f) => f.id === oldFilter.id);
    if (newFilter) {
      oldFilter.options = newFilter.options;
    }
  }
  return true;
};

const useFilterStore = create<FilterStoreState>((set, get) => ({
  // 초기 상태
  queryFilters: {},
  filters: [],
  tempFilters: [],
  isLoading: true,
  error: null,
  searchQueries: {},
  selectedTempCount: 0,
  selectedTempOptions: [],
  appliedFilterOrder: [], // 적용된 필터의 순서

  // 쿼리 필터 설정
  setQueryFilters: (filters: Record<string, string>) => {
    logger.info('setQueryFilters', filters);

    set({ queryFilters: filters });
  },

  // 필터 설정
  setFilters: (newFilters: Filter[] | null) =>
    set((state) => {
      const viewFilters = newFilters ? newFilters : [];

      logger.info('Current filters:', state.filters);
      logger.info('New filters:', viewFilters);

      const isSameFilters = deepEqualFilters(state.filters, viewFilters);
      logger.info('Filters changed:', !isSameFilters);

      if (!isSameFilters) {
        const tempFilters = structuredClone(viewFilters);
        logger.info('Updating filters and tempFilters');

        // 선택된 옵션 목록 계산
        const selectedOptions = calculateSelectedOptions(tempFilters);

        return {
          filters: viewFilters,
          tempFilters,
          selectedTempOptions: selectedOptions,
        };
      }
      return state;
    }),

  // 특정 필터 옵션 해제
  unCheckFilter: async (filterId: string, optionName: string) => {
    get().handleChangeCheckedTempFilter(filterId, optionName);
    get().applyTempFilters();
  },

  // 전체 필터 초기화
  resetFilters: async () => {
    logger.info('Resetting filters');

    get().resetTempFilters();
    get().applyTempFilters();
  },

  // 전체 임시 필터 초기화
  resetTempFilters: async () => {
    const tempFilters = get().tempFilters.map((f) => ({
      ...f,
      options: f.options.map((o) => ({
        ...o,
        selected: false,
        selectedAt: undefined,
      })),
    }));

    set(() => ({
      tempFilters: tempFilters,
      selectedTempCount: 0, // 선택 카운터 초기화
      selectedTempOptions: [], // 선택된 옵션 목록 초기화
    }));
    logger.info('resetTempFilters : tempFilters', tempFilters);
  },

  // 특정 임시 필터 초기화
  resetTempFilter: async (filterId) => {
    try {
      // 모든 옵션 업데이트
      set((state) => {
        const updatedTempFilters = state.tempFilters.map((filter) => {
          logger.info(
            'resetTempFilter : filter.id, filterId',
            filter.id,
            filterId,
          );
          logger.info('resetTempFilter : filter', filter);
          if (filter.id !== filterId) return filter;

          return {
            ...filter,
            options: filter.options.map((option) => {
              return {
                ...option,
                selected: false,
                selectedAt: undefined,
              };
            }),
          };
        });

        logger.info('updatedTempFilters', updatedTempFilters);

        return {
          tempFilters: updatedTempFilters,
          selectedTempCount: 0,
          selectedTempOptions: [],
        };
      });
    } catch (error) {
      logger.error('Failed to update filter:', error);
    }
  },

  // 임시 필터 체크 상태 변경 (선택 시간 추적 기능 추가)
  handleChangeCheckedTempFilter: async (
    filterId: string,
    optionName: string,
  ) => {
    try {
      // 특정 옵션만 업데이트
      set((state) => {
        const newSelectedCount = state.selectedTempCount + 1;

        const updatedTempFilters = state.tempFilters.map((filter) => {
          if (filter.id !== filterId) return filter;

          return {
            ...filter,
            options: filter.options.map((option) => {
              if (option.name !== optionName) return option;

              // 옵션 선택 시 타임스탬프 추가, 선택 해제 시 타임스탬프 제거
              if (!option.selected) {
                return {
                  ...option,
                  selected: true,
                  selectedAt: newSelectedCount, // 증가하는 카운터 사용 (타임스탬프 대신)
                };
              } else {
                return {
                  ...option,
                  selected: false,
                  selectedAt: undefined,
                };
              }
            }),
          };
        });

        logger.info('updatedTempFilters', updatedTempFilters);

        // 선택된 옵션 목록 다시 계산
        const selectedOptions = calculateSelectedOptions(updatedTempFilters);

        return {
          tempFilters: updatedTempFilters,
          selectedTempCount: newSelectedCount, // 카운터 증가
          selectedTempOptions: selectedOptions, // 선택된 옵션 목록 업데이트
        };
      });
    } catch (error) {
      logger.error('Failed to update filter:', error);
    }
  },

  // 임시 필터 적용
  applyTempFilters: async () => {
    const { tempFilters, filters } = get();
    logger.info('applyTempFilters', filters, tempFilters);
    logger.info('deepEqualFilters', deepEqualFilters(filters, tempFilters));
    if (!deepEqualFilters(filters, tempFilters)) {
      if (applySelectiveUpdates(filters, tempFilters)) {
        const queryParams = toRecord(
          extendFilters(filters, convertToFilter(get().queryFilters)),
        );
        logger.info('applyTempFilters : queryParams', queryParams);

        // 선택된 옵션 목록을 filters에 맞게 업데이트
        const selectedOptions = calculateSelectedOptions(filters);
        set({
          selectedTempOptions: selectedOptions,
          appliedFilterOrder: selectedOptions, // 적용된 필터의 순서도 업데이트
        });

        useProductStore.getState().fetchProducts(queryParams);
      }
    }
  },

  // 이전 필터 복구
  restoreOriginalFilters: async () => {
    logger.info('Restoring original filters');
    const { filters } = get();

    // 복원된 필터로 선택된 옵션 목록 다시 계산
    const selectedOptions = calculateSelectedOptions(filters);

    set({
      tempFilters: structuredClone(filters),
      searchQueries: {},
      selectedTempOptions: selectedOptions,
    });
  },

  // 선택된 옵션 목록 조회
  getSelectedOptions: (mode: 'filters' | 'tempFilters') => {
    if (mode === 'tempFilters') {
      return get().selectedTempOptions;
    }

    // 테이블 모드일 때는 filters에서 선택된 항목만 표시
    const selectedFilters = get()
      .filters.map((filter) =>
        filter.options
          .filter((option) => option.selected)
          .map((option) => ({
            filterId: filter.id,
            optionName: option.name,
            selectedAt: Date.now(), // 임시값, 실제로는 필요없음
            filterName: filter.name,
          })),
      )
      .flat();

    // appliedFilterOrder의 순서를 기준으로 정렬
    const orderedOptions = get()
      .appliedFilterOrder.map((tempOption) =>
        selectedFilters.find(
          (filter) =>
            filter.filterId === tempOption.filterId &&
            filter.optionName === tempOption.optionName,
        ),
      )
      .filter(
        (option): option is NonNullable<typeof option> => option !== undefined,
      );

    // appliedFilterOrder에 없는 항목들을 뒤에 추가
    const remainingOptions = selectedFilters.filter(
      (filter) =>
        !get().appliedFilterOrder.some(
          (tempOption) =>
            tempOption.filterId === filter.filterId &&
            tempOption.optionName === filter.optionName,
        ),
    );

    return [...orderedOptions, ...remainingOptions];
  },
}));

export { useFilterStore };
